/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Button,
  Text,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Divider,
} from "@chakra-ui/react";
import { FiPlus, FiEdit2, FiRefreshCw } from "react-icons/fi";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { useUserRole } from "hooks/useUserRole";
import BayiContextMenu from "components/ContextMenu/BayiContextMenu";
import BayiDurumuModal from "components/Modals/BayiYonetimi/BayiDurumuModal";

export default function BayiYonetimi() {
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  const [bayiler, setBayiler] = useState([]);
  const [loading, setLoading] = useState(false);
  const userRole = useUserRole();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDurumModalOpen, setIsDurumModalOpen] = useState(false);
  const [selectedBayi, setSelectedBayi] = useState(null);
  const [contextMenu, setContextMenu] = useState({ position: null, bayi: null });
  
  // Form states
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    bayi_tipi: "yetkili_bayi", // Default: yetkili_bayi
    sms_monthly_limit: 100, // Default: 100
  });
  const [formLoading, setFormLoading] = useState(false);

  // Rastgele şifre üret
  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  // Kullanıcı rolünü kontrol et
  useEffect(() => {
    // Sadece admin erişebilir
    if (userRole && userRole !== "admin") {
      toast({
        title: "Erişim Reddedildi",
        description: "Bu sayfaya sadece admin kullanıcılar erişebilir.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      // Ana sayfaya yönlendir
      window.location.href = "/admin/dashboard";
    }
  }, [userRole, toast]);

  // Bayileri yükle
  const loadBayiler = useCallback(async () => {
    if (userRole !== "admin") return;
    
    setLoading(true);
    try {
      const response = await api.get("/api/bayi-yonetimi/bayiler");
      if (response.ok) {
        const data = await response.json();
        setBayiler(data);
      } else {
        throw new Error("Bayiler yüklenemedi");
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message || "Bayiler yüklenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [userRole, toast]);

  useEffect(() => {
    if (userRole === "admin") {
      loadBayiler();
    }
  }, [userRole, loadBayiler]);

  // Yeni bayi oluştur
  const handleCreate = async () => {
    // Şifre yoksa rastgele şifre üret
    let passwordToUse = formData.password;
    if (!passwordToUse) {
      passwordToUse = generateRandomPassword();
      setFormData({ ...formData, password: passwordToUse });
    }

    if (!formData.username || !passwordToUse) {
      toast({
        title: "Eksik Bilgi",
        description: "Kullanıcı adı gereklidir",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.post("/api/bayi-yonetimi/bayiler", {
        username: formData.username,
        password: passwordToUse,
        bayi_tipi: formData.bayi_tipi,
        sms_monthly_limit: formData.sms_monthly_limit || 100,
      });

      if (response.ok) {
        toast({
          title: "Başarılı",
          description: "Bayi başarıyla oluşturuldu",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsCreateOpen(false);
        setFormData({ username: "", password: "", bayi_tipi: "yetkili_bayi", sms_monthly_limit: 100 });
        loadBayiler();
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Bayi oluşturulamadı");
      }
    } catch (error) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "Bayi oluşturma hatası");
    } finally {
      setFormLoading(false);
    }
  };

  // Bayi güncelle
  const handleUpdate = async () => {
    if (!selectedBayi) return;
    
    if (!formData.username) {
      toast({
        title: "Eksik Bilgi",
        description: "Kullanıcı adı gereklidir",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFormLoading(true);
    try {
      const updateData = { username: formData.username };
      if (formData.password) {
        updateData.password = formData.password;
      }
      if (formData.bayi_tipi !== undefined) {
        updateData.bayi_tipi = formData.bayi_tipi;
      }
      if (formData.sms_monthly_limit !== undefined) {
        updateData.sms_monthly_limit = formData.sms_monthly_limit === "" ? null : parseInt(formData.sms_monthly_limit);
      }

      const response = await api.put(
        `/api/bayi-yonetimi/bayiler/${selectedBayi.id}`,
        updateData
      );

      if (response.ok) {
        toast({
          title: "Başarılı",
          description: "Bayi başarıyla güncellendi",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsEditOpen(false);
        setSelectedBayi(null);
        setFormData({ username: "", password: "", bayi_tipi: "yetkili_bayi", sms_monthly_limit: 100 });
        loadBayiler();
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Bayi güncellenemedi");
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message || "Bayi güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setFormLoading(false);
    }
  };

  // Edit modal aç
  const openEditModal = (bayi) => {
    setSelectedBayi(bayi);
    const randomPassword = generateRandomPassword();
    setFormData({
      username: bayi.username,
      password: randomPassword, // Random şifre oluştur
      bayi_tipi: bayi.bayi_tipi || "yetkili_bayi",
      sms_monthly_limit: bayi.sms_monthly_limit || 100,
    });
    setIsEditOpen(true);
  };

  // Context menu işlemleri
  const handleContextMenu = (e, bayi) => {
    e.preventDefault();
    setContextMenu({
      position: { x: e.clientX, y: e.clientY },
      bayi: bayi,
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ position: null, bayi: null });
  };

  const textColor = useColorModeValue("gray.700", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const statsBg = useColorModeValue("gray.50", "navy.900");

  // Admin değilse sayfayı gösterme
  if (userRole !== "admin") {
    return (
      <Box p={4}>
        <Text>Yükleniyor...</Text>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex direction="column" gap="20px">
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap="20px">
          <VStack align="start" spacing={2}>
            <Text 
              fontSize={{ base: "xl", md: "2xl", lg: "3xl" }} 
              fontWeight="bold" 
              color="white"
              letterSpacing="-0.5px"
            >
            Bayi Yönetimi
          </Text>
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color="white"
              fontWeight="normal"
              opacity={0.9}
            >
              Bayi hesaplarını yönetin ve istatistikleri görüntüleyin
            </Text>
          </VStack>
          <HStack>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={loadBayiler}
              isLoading={loading}
              bg="white"
              color="blue.600"
              _hover={{ bg: "gray.50" }}
            >
              Yenile
            </Button>
            <Button
              leftIcon={<FiPlus />}
              onClick={() => {
                const randomPassword = generateRandomPassword();
                setFormData({ username: "", password: randomPassword, bayi_tipi: "yetkili_bayi" });
                setIsCreateOpen(true);
              }}
              colorScheme="blue"
            >
              Yeni Bayi Ekle
            </Button>
          </HStack>
        </Flex>

        {/* Bayiler Tablosu */}
        <Box bg={cardBg} borderRadius="15px" p="20px" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          {loading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="xl" />
            </Flex>
          ) : bayiler.length === 0 ? (
            <VStack py={12} spacing={4}>
              <Text textAlign="center" color="gray.500" fontSize="lg">
              Henüz bayi bulunmamaktadır
            </Text>
              <Button
                leftIcon={<FiPlus />}
                onClick={() => {
                  const randomPassword = generateRandomPassword();
                  setFormData({ username: "", password: randomPassword, bayi_tipi: "yetkili_bayi" });
                  setIsCreateOpen(true);
                }}
                colorScheme="blue"
                size="sm"
              >
                İlk Bayiyi Ekle
              </Button>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  Bayi Listesi ({bayiler.length})
                </Text>
              </Flex>
              <Divider />
              <Box overflowX="auto">
                <Table variant="simple" size="md">
              <Thead>
                    <Tr bg={statsBg}>
                      <Th color={textColor} fontWeight="semibold">ID</Th>
                      <Th color={textColor} fontWeight="semibold">Kullanıcı Adı</Th>
                      <Th color={textColor} fontWeight="semibold">Bayi Tipi</Th>
                </Tr>
              </Thead>
              <Tbody>
                {bayiler.map((bayi) => (
                    <Tr 
                      key={bayi.id} 
                      _hover={{ bg: statsBg }}
                      onContextMenu={(e) => handleContextMenu(e, bayi)}
                      cursor="pointer"
                    >
                      <Td>
                        <Text fontWeight="semibold" color={textColor}>
                          {bayi.id}
                        </Text>
                      </Td>
                    <Td>
                        <Text fontWeight="medium" color={textColor}>
                          {bayi.username}
                        </Text>
                    </Td>
                    <Td>
                        <Badge 
                          colorScheme={bayi.bayi_tipi === "saas_bayi" ? "green" : "orange"} 
                          borderRadius="full" 
                          px={3} 
                          py={1}
                        >
                          {bayi.bayi_tipi === "saas_bayi" ? "Saas Bayi" : bayi.bayi_tipi === "yetkili_bayi" ? "Yetkili Bayi" : "-"}
                        </Badge>
                      </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </Box>
            </VStack>
          )}
        </Box>
      </Flex>

      {/* Yeni Bayi Ekle Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>Yeni Bayi Ekle</ModalHeader>
          <ModalCloseButton />
          <Divider />
          <ModalBody py={6}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color={textColor}>Kullanıcı Adı</FormLabel>
                <Input
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  placeholder="Kullanıcı adı (min 5 karakter)"
                  size="md"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Sadece harf, rakam ve alt çizgi kullanılabilir
                </Text>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color={textColor}>Şifre</FormLabel>
                <Input
                  type="text"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Otomatik oluşturuldu"
                  size="md"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Rastgele şifre otomatik oluşturuldu. İsterseniz değiştirebilirsiniz.
                </Text>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color={textColor}>Bayi Tipi</FormLabel>
                <Select
                  value={formData.bayi_tipi}
                  onChange={(e) =>
                    setFormData({ ...formData, bayi_tipi: e.target.value })
                  }
                  size="md"
                >
                  <option value="yetkili_bayi">Yetkili Bayi</option>
                  <option value="saas_bayi">Saas Bayi</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Yetkili Bayi: Stok ve Sim yönetimi göremez. Saas Bayi: Stok ve Sim yönetimi görebilir.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsCreateOpen(false)}
            >
              İptal
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreate}
              isLoading={formLoading}
              leftIcon={<FiPlus />}
            >
              Oluştur
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bayi Düzenle Modal */}
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>
            Bayi Düzenle: {selectedBayi?.username}
          </ModalHeader>
          <ModalCloseButton />
          <Divider />
          <ModalBody py={6}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color={textColor}>Kullanıcı Adı</FormLabel>
                <Input
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  placeholder="Kullanıcı adı"
                  size="md"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium" color={textColor}>Yeni Şifre</FormLabel>
                <Input
                  type="text"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Otomatik oluşturuldu"
                  size="md"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Rastgele şifre otomatik oluşturuldu. İsterseniz değiştirebilirsiniz.
                </Text>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color={textColor}>Bayi Tipi</FormLabel>
                <Select
                  value={formData.bayi_tipi}
                  onChange={(e) =>
                    setFormData({ ...formData, bayi_tipi: e.target.value })
                  }
                  size="md"
                >
                  <option value="yetkili_bayi">Yetkili Bayi</option>
                  <option value="saas_bayi">Saas Bayi</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Yetkili Bayi: Stok ve Sim yönetimi göremez. Saas Bayi: Stok ve Sim yönetimi görebilir.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsEditOpen(false)}
            >
              İptal
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdate}
              isLoading={formLoading}
              leftIcon={<FiEdit2 />}
            >
              Güncelle
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bayi Context Menu */}
      <BayiContextMenu
        position={contextMenu.position}
        data={contextMenu.bayi}
        onClose={closeContextMenu}
        onEdit={openEditModal}
        onGenelBilgiler={(bayi) => {
          setSelectedBayi(bayi);
          setIsDurumModalOpen(true);
        }}
      />

      {/* Bayi Durumu Modal */}
      <BayiDurumuModal
        isOpen={isDurumModalOpen}
        onClose={() => {
          setIsDurumModalOpen(false);
          setSelectedBayi(null);
        }}
        bayi={selectedBayi}
        onUpdate={loadBayiler}
      />
    </Box>
  );
}

